import React, { useState } from 'react';

const CARD_LABELS = ['POINT 1', 'DEVELOPMENT 1', 'POINT 2', 'DEVELOPMENT 2'];

function getScoreColour(score, max) {
  const pct = score / max;
  if (pct >= 0.75) return { text: '#3fb950', bg: 'rgba(63,185,80,0.10)',  border: '#3fb950' };
  if (pct >= 0.5)  return { text: '#d29922', bg: 'rgba(210,153,34,0.10)', border: '#d29922' };
  return             { text: '#f85149', bg: 'rgba(248,81,73,0.10)',  border: '#f85149' };
}

export default function ExamQuestionAccordion({ index, attempt }) {
  const [open, setOpen] = useState(false);

  const snapshot      = attempt.questionSnapshot ?? {};
  const studentAnswer = attempt.studentAnswer ?? {};
  const feedback      = attempt.markingResult ?? null;
  const cardFb        = feedback?.card_feedback ?? {};
  const score         = feedback?.total ?? null;
  const isPending     = attempt.markingStatus !== 'completed' && attempt.markingStatus !== 'error';
  const isError       = attempt.markingStatus === 'error';

  const c = score != null ? getScoreColour(score, 4) : null;

  return (
    <div className="rounded-xl border border-[#30363d] overflow-hidden">
      {/* Accordion header — always visible */}
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-4 px-4 py-3 bg-[#161b22] hover:bg-[#1c2128] transition-colors text-left group"
      >
        <div className="flex items-center gap-3 min-w-0">
          {/* Score badge */}
          {isPending ? (
            <div className="flex-shrink-0 w-10 h-10 rounded-full border-2 border-[#30363d] bg-[#0d1117] flex items-center justify-center">
              <span className="w-3 h-3 border-2 border-[#30363d] border-t-[#8b949e] rounded-full animate-spin" />
            </div>
          ) : isError ? (
            <div className="flex-shrink-0 w-10 h-10 rounded-full border-2 border-[#f85149]/40 bg-[#f85149]/5 flex items-center justify-center">
              <span className="text-[#f85149] text-[10px] font-mono">err</span>
            </div>
          ) : (
            <div
              className="flex-shrink-0 w-10 h-10 rounded-full border-2 flex items-center justify-center"
              style={{ borderColor: c.border, backgroundColor: c.bg }}
            >
              <span className="text-sm font-bold" style={{ color: c.text }}>{score}</span>
              <span className="text-[8px] text-[#8b949e] leading-none">/4</span>
            </div>
          )}

          {/* Question label + text */}
          <div className="min-w-0">
            <div className="text-[10px] font-mono text-[#8b949e] mb-0.5">
              Q{index + 1}
              {snapshot.year ? ` · ${snapshot.year} Paper ${snapshot.paper} · ${snapshot.question_number}` : ''}
            </div>
            <p className="text-[#e6edf3] text-xs font-medium leading-snug line-clamp-1">
              {snapshot.question_text ?? `Question ${index + 1}`}
            </p>
          </div>
        </div>

        {/* Chevron */}
        <svg
          className={`w-4 h-4 flex-shrink-0 text-[#484f58] group-hover:text-[#8b949e] transition-transform ${open ? 'rotate-180' : ''}`}
          fill="none" viewBox="0 0 24 24" stroke="currentColor"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Accordion body */}
      {open && (
        <div className="px-4 py-4 space-y-5 bg-[#0d1117] border-t border-[#30363d]">

          {/* ── Your Answer ──────────────────────────────────── */}
          <section>
            <h3 className="text-[10px] font-mono font-semibold text-[#8b949e] uppercase tracking-wider mb-2">
              Your Answer
            </h3>
            <div className="space-y-2">
              {CARD_LABELS.map((lbl) => {
                const text = studentAnswer[lbl]?.trim();
                if (!text) return null;
                return (
                  <div key={lbl} className="rounded-lg border border-[#30363d] bg-[#161b22] px-3 py-2">
                    <div className="text-[9px] font-mono font-semibold text-[#484f58] uppercase tracking-wider mb-1">{lbl}</div>
                    <p className="text-[#e6edf3] text-xs leading-relaxed">{text}</p>
                  </div>
                );
              })}
              {CARD_LABELS.every((lbl) => !studentAnswer[lbl]?.trim()) && (
                <p className="text-[#484f58] text-xs italic">No answer recorded.</p>
              )}
            </div>
          </section>

          {/* ── Mark Scheme ──────────────────────────────────── */}
          {snapshot.mark_scheme && (
            <section>
              <h3 className="text-[10px] font-mono font-semibold text-[#8b949e] uppercase tracking-wider mb-2">
                Mark Scheme
              </h3>
              <div className="rounded-lg border border-[#30363d] bg-[#161b22] px-3 py-3">
                <p className="text-[#8b949e] text-xs leading-relaxed">{snapshot.mark_scheme}</p>
              </div>
            </section>
          )}

          {/* ── AI Feedback ──────────────────────────────────── */}
          {attempt.markingStatus === 'completed' && feedback && (
            <section>
              <h3 className="text-[10px] font-mono font-semibold text-[#8b949e] uppercase tracking-wider mb-2">
                AI Feedback
              </h3>
              <div className="space-y-2">
                {CARD_LABELS.map((lbl) => {
                  const cf = cardFb[lbl] ?? {};
                  const awarded = cf.marks ?? 0;
                  return (
                    <div key={lbl} className="rounded-lg border border-[#30363d] bg-[#161b22] px-3 py-2">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[10px] font-mono text-slate-400 font-semibold">{lbl}</span>
                        <span
                          className={`text-[10px] font-mono font-bold px-1.5 py-0.5 rounded ${
                            awarded === 1
                              ? 'text-[#3fb950] bg-[#3fb950]/10'
                              : 'text-[#f85149] bg-[#f85149]/10'
                          }`}
                        >
                          [{awarded}/1]
                        </span>
                      </div>
                      {cf.comment && (
                        <p className="text-[#8b949e] text-xs leading-relaxed">{cf.comment}</p>
                      )}
                      {cf.minimal_improvement && (
                        <p className="text-[#d29922] text-[10px] font-mono mt-1.5 leading-relaxed">
                          ✏ {cf.minimal_improvement}
                        </p>
                      )}
                    </div>
                  );
                })}
                {feedback.examiner_note && (
                  <div className="rounded-lg border border-[#30363d] bg-[#161b22] px-3 py-2.5">
                    <div className="text-[10px] font-mono leading-relaxed">
                      <span className="text-[#8b949e]">examiner_note</span>
                      <span className="text-[#484f58]"> » </span>
                      <span className="text-[#e6edf3]">{feedback.examiner_note}</span>
                    </div>
                  </div>
                )}
              </div>
            </section>
          )}

          {attempt.markingStatus === 'error' && (
            <p className="text-[#f85149] text-xs font-mono">Marking failed for this question.</p>
          )}

          {isPending && (
            <p className="text-[#8b949e] text-xs font-mono">Marking in progress…</p>
          )}
        </div>
      )}
    </div>
  );
}
